import { Color3, Mesh, Vector3 } from '@babylonjs/core';
import { createArenaScene, createRobotMesh } from './arena';
import type { ArenaScene } from './arena';
import { buildLineTrack } from './arenaGeometry';
import type { LineArena } from '../data/arenas';
import type { RobotValues, RulePack, SimCallbacks, SimEvent } from '../types';

function num(v: number | string | boolean | undefined, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Kinematic line-follower sim: PID steering on a curved track with sensor simulation. */
export class LineSim {
  private arena: ArenaScene | null = null;
  private disposed = false;
  private finished = false;
  private events: SimEvent[] = [];
  private speedSeries: number[] = [];
  private time = 0;
  private path: Vector3[] = [];
  private robot: Mesh | null = null;

  private pos = new Vector3(0, 0.05, 0);
  private heading = 0;
  private trackIdx = 0;
  private prevError = 0;
  private losses = 0;
  private flips = 0;
  private prevSteerSign = 0;
  private maxSpeed = 0;
  private errSum = 0;
  private errCount = 0;
  private recoverUntil = 0;
  private hudAccum = 0;
  private speedAccum = 0;
  private lineWidthCm = 2.5;

  private canvas: HTMLCanvasElement;
  private pack: RulePack;
  private cfg: RobotValues;
  private arenaDef: LineArena;
  private cb: SimCallbacks;

  constructor(canvas: HTMLCanvasElement, pack: RulePack, cfg: RobotValues, arenaDef: LineArena, cb: SimCallbacks) {
    this.canvas = canvas;
    this.pack = pack;
    this.cfg = cfg;
    this.arenaDef = arenaDef;
    this.cb = cb;
  }

  async start(): Promise<void> {
    const arena = createArenaScene(this.canvas, 3.4);
    this.arena = arena;
    const { scene, camera } = arena;
    camera.beta = 0.7;

    /* ---------- track ---------- */
    this.lineWidthCm = num(this.arenaDef.lineWidthCm ?? this.pack.track?.lineWidthCm, 2.5);
    const track = buildLineTrack(scene, this.arenaDef, this.lineWidthCm);
    this.path = track.path;

    /* ---------- robot ---------- */
    this.robot = createRobotMesh(scene, 'lf', { color: new Color3(0.1, 0.6, 1), width: 0.12, length: 0.15, height: 0.06 });
    this.pos = this.path[0].clone();
    this.pos.y = 0.04;
    const t0 = this.path[1].subtract(this.path[0]);
    this.heading = Math.atan2(t0.x, t0.z);

    this.event('info', `Run started on ${this.arenaDef.name} — follow the line to the red finish marker`);

    arena.scene.onBeforeRenderObservable.add(() => {
      if (this.finished || this.disposed) return;
      const dt = Math.min(arena.engine.getDeltaTime() / 1000, 0.05);
      this.step(dt);
    });
  }

  private step(dt: number): void {
    this.time += dt;
    const kp = num(this.cfg.kp, 3);
    const kd = num(this.cfg.kd, 1.5);
    const base = num(this.cfg.baseSpeed, 0.6);
    const slow = num(this.cfg.curveSlowdown, 0.4);
    const maxTime = num(this.pack.match?.maxTimeSec, 120);
    const maxLosses = num(this.pack.match?.maxLineLosses, 5);

    /* nearest path point (monotonic search window) */
    let best = this.trackIdx;
    let bestD = Infinity;
    for (let i = this.trackIdx; i < Math.min(this.trackIdx + 30, this.path.length); i++) {
      const d = Vector3.DistanceSquared(this.path[i], this.pos);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    this.trackIdx = best;
    const target = this.path[best];
    const next = this.path[Math.min(best + 1, this.path.length - 1)];
    const tan = next.subtract(this.path[Math.max(best - 1, 0)]).normalize();
    // signed lateral error (m): + means line is to the left of the robot
    const toRobot = this.pos.subtract(target);
    const error = toRobot.x * tan.z - toRobot.z * tan.x;

    this.errSum += Math.abs(error);
    this.errCount++;

    /* PID steering */
    const dErr = (error - this.prevError) / Math.max(dt, 0.001);
    this.prevError = error;
    const steer = -(kp * error * 8 + kd * dErr * 0.8);
    const steerSign = Math.sign(steer) * (Math.abs(steer) > 1.2 ? 1 : 0);
    if (steerSign !== 0 && this.prevSteerSign !== 0 && steerSign !== this.prevSteerSign) this.flips++;
    if (steerSign !== 0) this.prevSteerSign = steerSign;

    const recovering = this.time < this.recoverUntil;
    const speed = recovering ? 0.12 : base * (1 - slow * Math.min(1, Math.abs(steer) / 3));
    this.maxSpeed = Math.max(this.maxSpeed, speed);

    this.heading += Math.max(-3.5, Math.min(3.5, steer)) * dt;
    this.pos.x += Math.sin(this.heading) * speed * dt;
    this.pos.z += Math.cos(this.heading) * speed * dt;

    /* line loss */
    const lineHalf = this.lineWidthCm / 200 + 0.045;
    if (Math.abs(error) > lineHalf && !recovering) {
      this.losses++;
      this.event('warn', `Line lost at ${this.time.toFixed(1)}s (loss ${this.losses}/${maxLosses})`);
      this.recoverUntil = this.time + 0.8;
      // snap gently back toward the line
      this.pos.x = target.x + (this.pos.x - target.x) * 0.2;
      this.pos.z = target.z + (this.pos.z - target.z) * 0.2;
      this.heading = Math.atan2(tan.x, tan.z);
      this.time += num(this.pack.match?.lineLossPenaltySec, 3); // time penalty
      if (this.losses >= maxLosses) {
        this.finish(false, 'Run failed — too many line losses');
        return;
      }
    }

    /* render */
    if (this.robot) {
      this.robot.position.copyFrom(this.pos);
      this.robot.rotation = new Vector3(0, this.heading, 0);
    }
    if (this.arena) this.arena.camera.target = this.pos.clone();

    /* sampling + HUD */
    this.speedAccum += dt;
    if (this.speedAccum >= 0.1) {
      this.speedAccum = 0;
      this.speedSeries.push(speed);
    }
    this.hudAccum += dt;
    if (this.hudAccum >= 0.12) {
      this.hudAccum = 0;
      const nSensors = num(this.cfg.sensors, 5);
      const span = 0.08;
      let bits = '';
      for (let s = 0; s < nSensors; s++) {
        const offset = (s / (nSensors - 1) - 0.5) * span;
        bits += Math.abs(error + offset) < lineHalf * 0.8 ? '█' : '·';
      }
      this.cb.onTick({
        timeSec: this.time,
        status: recovering ? 'RECOVERING' : 'FOLLOWING',
        sensors: {
          'Sensor array': bits,
          'Line error': `${(error * 100).toFixed(1)} cm`,
          Speed: `${speed.toFixed(2)} m/s`,
          'Line losses': `${this.losses}`,
          Progress: `${Math.round((best / (this.path.length - 1)) * 100)}%`,
        },
      });
    }

    if (best >= this.path.length - 2) {
      this.finish(true, 'Finish line crossed!');
      return;
    }
    if (this.time > maxTime) this.finish(false, 'Time limit exceeded');
  }

  private finish(won: boolean, outcome: string): void {
    if (this.finished) return;
    this.finished = true;
    const sc = this.pack.scoring ?? {};
    const avgErr = this.errCount ? (this.errSum / this.errCount) * 100 : 0;
    const oscPerSec = this.flips / Math.max(this.time, 1);
    const smooth = oscPerSec < 0.6;

    let score = 0;
    if (won) {
      score += num(sc.finishBase, 100);
      score += Math.max(0, Math.round(60 - this.time)) * num(sc.secondBonusUnder60, 1);
      if (smooth) score += num(sc.smoothDrivingBonus, 10);
    }
    score -= this.losses * num(sc.lineLossPenalty, 5);

    this.event(won ? 'good' : 'bad', outcome);
    this.cb.onEnd({
      competition: 'line',
      outcome,
      won,
      score,
      timeSec: this.time,
      metrics: {
        Time: `${this.time.toFixed(1)} s`,
        'Line losses': `${this.losses}`,
        'Max speed': `${this.maxSpeed.toFixed(2)} m/s`,
        'Average error': `${avgErr.toFixed(1)} cm`,
        Oscillation: `${oscPerSec.toFixed(2)} direction flips/s ${smooth ? '(smooth ✓)' : '(shaky)'}`,
      },
      raw: { losses: this.losses, oscPerSec, avgErr, time: this.time, maxSpeed: this.maxSpeed },
      events: this.events,
      speedSeries: this.speedSeries,
    });
  }

  private event(type: SimEvent['type'], msg: string): void {
    this.events.push({ t: this.time, type, msg });
  }

  dispose(): void {
    this.disposed = true;
    this.arena?.dispose();
    this.arena = null;
  }
}
